import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Spin, Table, Tag, Empty, message, Button, Timeline, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService } from "@iso/services";
import moment from "moment";
import { InvestigationFormWrapper } from "./InvestigationForm.styles";
import ReallocateModal from "./ReallocateModal";
import CompleteActionModal from "./CompleteActionModal";

const STATUS_COLORS = {
  assigned: "default",
  in_progress: "processing",
  completed: "#0B2A1B",
};

const STATUS_LABELS = {
  assigned: "Assigned",
  in_progress: "In Progress",
  completed: "Completed",
};

const REALLOCATABLE_STATUSES = ["assigned", "in_progress"];

const CorrectiveActionsPage = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reallocateTarget, setReallocateTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (incidentId) loadData();
  }, [incidentId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentData, investigationData, actionsData] = await Promise.all([
        incidentService.getById(incidentId),
        incidentService.getInvestigation(incidentId).catch(() => null),
        incidentService.getCorrectiveActions(incidentId),
      ]);
      setIncident(incidentData);
      setInvestigation(investigationData);
      setActions(actionsData || []);
    } catch (err) {
      console.error("Failed to load corrective actions data:", err);
      message.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const isApproved = investigation?.status === "approved";

  const handleReallocateSaved = () => {
    loadData();
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => <span style={{ whiteSpace: "pre-wrap" }}>{text}</span>,
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_to_name",
      key: "assigned_to_name",
      width: 160,
      render: (text) => text || "-",
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 130,
      render: (text) => (text ? moment(text).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>
          {STATUS_LABELS[status] || status}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 220,
      render: (_, record) => {
        const canReallocate = REALLOCATABLE_STATUSES.includes(record.status);
        const hasHistory =
          record.reallocation_history && record.reallocation_history.length > 0;
        return (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canReallocate && (
              <Tooltip title="Mark as complete">
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  type="primary"
                  ghost
                  onClick={() => setCompleteTarget(record)}
                >
                  Complete
                </Button>
              </Tooltip>
            )}
            {canReallocate && (
              <Tooltip title="Re-allocate to another person">
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => setReallocateTarget(record)}
                >
                  Re-allocate
                </Button>
              </Tooltip>
            )}
            {hasHistory && (
              <Tooltip title="View re-allocation history">
                <Button
                  size="small"
                  icon={<HistoryOutlined />}
                  type="text"
                  onClick={() =>
                    setExpandedRows((prev) =>
                      prev.includes(record.id)
                        ? prev.filter((id) => id !== record.id)
                        : [...prev, record.id]
                    )
                  }
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    const history = record.reallocation_history || [];
    if (history.length === 0) return null;

    return (
      <div style={{ padding: "12px 24px", background: "#f9fafb" }}>
        <p
          style={{
            fontWeight: 600,
            color: "#344054",
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Re-allocation History
        </p>
        <Timeline style={{ marginLeft: 8 }}>
          {history.map((entry) => (
            <Timeline.Item key={entry.id} color="#0B2A1B">
              <div style={{ fontSize: 13 }}>
                <span style={{ color: "#101828", fontWeight: 500 }}>
                  {entry.reallocated_by_name || "Unknown"}
                </span>{" "}
                re-allocated from{" "}
                <span style={{ color: "#344054" }}>
                  {entry.from_user_name || "Unknown"}
                </span>{" "}
                to{" "}
                <span style={{ color: "#344054", fontWeight: 500 }}>
                  {entry.to_user_name || "Unknown"}
                </span>
                {entry.new_due_date !== entry.old_due_date && (
                  <span style={{ color: "#667085" }}>
                    {" "}
                    · Due date changed to{" "}
                    {moment(entry.new_due_date).format("DD/MM/YYYY")}
                  </span>
                )}
                <div style={{ color: "#667085", marginTop: 2 }}>
                  <em>Reason: {entry.reason}</em>
                </div>
                <div style={{ color: "#98a2b3", fontSize: 11, marginTop: 2 }}>
                  {moment.utc(entry.created_at).local().format("DD/MM/YYYY h:mm A")}
                </div>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </div>
    );
  };

  if (loading) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="loading-container">
              <Spin size="large" />
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationFormWrapper>
            <div className="not-found-container">
              <h2 className="not-found-title">Incident not found</h2>
              <span
                className="not-found-link"
                onClick={() => history.push("/incidents")}
              >
                Return to incidents
              </span>
            </div>
          </InvestigationFormWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationFormWrapper>
          <button
            className="inv-back-button"
            onClick={() => history.push("/incidents")}
          >
            <ArrowLeftOutlined /> Back to Incidents
          </button>

          <div className="inv-header" style={{ marginBottom: 24 }}>
            <div>
              <h1 className="inv-title">Corrective Actions</h1>
              <p className="inv-subtitle">
                {incident.report_number} — {incident.description || ""}
              </p>
            </div>
          </div>

          {!isApproved ? (
            <div
              className="form-card"
              style={{ textAlign: "center", padding: 48 }}
            >
              <ClockCircleOutlined
                style={{ fontSize: 48, color: "#d0d5dd", marginBottom: 16 }}
              />
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#101828",
                  marginBottom: 8,
                }}
              >
                Investigation Not Complete
              </h3>
              <p style={{ fontSize: 14, color: "#667085" }}>
                Corrective actions will be available once the investigation has
                been approved by a Safety Admin.
              </p>
            </div>
          ) : actions.length === 0 ? (
            <div
              className="form-card"
              style={{ textAlign: "center", padding: 48 }}
            >
              <Empty description="No corrective actions have been recorded for this incident." />
            </div>
          ) : (
            <div
              className="form-card"
              style={{ padding: 0, overflow: "hidden" }}
            >
              <Table
                dataSource={actions}
                columns={columns}
                rowKey="id"
                pagination={false}
                style={{ borderRadius: 12 }}
                expandable={{
                  expandedRowRender,
                  expandedRowKeys: expandedRows,
                  showExpandColumn: false,
                  rowExpandable: (record) =>
                    record.reallocation_history &&
                    record.reallocation_history.length > 0,
                }}
              />
            </div>
          )}
        </InvestigationFormWrapper>
      </Box>

      <ReallocateModal
        visible={!!reallocateTarget}
        action={reallocateTarget}
        incident={incident}
        onCancel={() => setReallocateTarget(null)}
        onSaved={handleReallocateSaved}
      />

      <CompleteActionModal
        visible={!!completeTarget}
        action={completeTarget}
        onCancel={() => setCompleteTarget(null)}
        onSaved={() => {
          setCompleteTarget(null);
          loadData();
        }}
      />
    </LayoutWrapper>
  );
};

export default CorrectiveActionsPage;
